import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Input, Modal, List, Tag, Space, Tooltip, Typography, Spin, Alert, Badge } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from '../components/StandardButton';
import { toast } from "react-hot-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";

const { TextArea } = Input;
const { Text } = Typography;

const JSPlayground = () => {
  const [code, setCode] = useState(`let sum = 0;
let count = 10000;

for (let i = 1; i <= count; i++) {
  sum += i;
}

console.log("Multiply:", sum*count);

return sum/count;`);
  const [output, setOutput] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [historyVisible, setHistoryVisible] = useState(false);
  const [history, setHistory] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState({
    history: false,
    shares: false,
    save: false,
    run: false,
    share: false,
    loadingShare: false
  });
  const [error, setError] = useState(null);
  const [isSharedView, setIsSharedView] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  
  // Use refs to track current state
  const codeRef = useRef(code);
  const outputRef = useRef(output);
  const runTimeoutRef = useRef(null);

  // Update refs when state changes
  useEffect(() => {
    codeRef.current = code;
  }, [code]);
  
  useEffect(() => {
    outputRef.current = output;
  }, [output]);

  // Get user data from localStorage (same as API interceptor)
  const getUserData = () => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      return userProfileData;
    } catch (_) {
      return null;
    }
  };

  // Get authentication status
  const isAuthenticated = () => {
    const userData = getUserData();
    if (!userData?.id) return false;
    
    // Check for either Google ID or LinkedIn ID
    const hasGoogleId = !!userData.google_id;
    const hasLinkedInId = !!userData.linkedin_id;
    
    return userData.id && (hasGoogleId || hasLinkedInId);
  };

  // Safe code execution function
  const executeCode = useCallback((codeToExecute) => {
    try {
      const logs = [];

      // Create a safe execution context
      const context = {
        console: {
          log: (...args) => logs.push(args.join(" "))
        }
      };

      // Wrap the code in a function with error handling
      const wrappedCode = `
        try {
          ${codeToExecute}
        } catch (error) {
          return "Error: " + error.message;
        }
      `;

      // Create function with limited context
      const fn = new Function(...Object.keys(context), `
        "use strict";
        ${wrappedCode}
      `);

      // Execute the function with context
      let result;
      try {
        result = fn(...Object.values(context));
      } catch (execError) {
        result = `Execution Error: ${execError.message}`;
      }

      const safeStringify = (value) => {
        const seen = new WeakSet();
        const replacer = (key, val) => {
          if (typeof val === "object" && val !== null) {
            if (seen.has(val)) return "[Circular]";
            seen.add(val);
          }
          if (typeof val === "function") return `[Function: ${val.name || 'anonymous'}]`;
          if (typeof val === "undefined") return "undefined";
          if (val === Infinity) return "Infinity";
          if (val === -Infinity) return "-Infinity";
          if (isNaN(val)) return "NaN";
          return val;
        };
        
        try {
          return JSON.stringify(value, replacer, 2);
        } catch (err) {
          return String(value);
        }
      };

      const logSection = logs.length
        ? `<br/><br/><strong>${t('console_logs')}:</strong><br/>${logs.map(log => `<div style="color: #888;">${log}</div>`).join("")}`
        : "";

      const resultStr = typeof result === 'undefined' ? 'undefined' : safeStringify(result);
      const finalOutput = `<strong>${t('output')}:</strong> ${resultStr}${logSection}`;
      
      return { output: finalOutput, success: true };
    } catch (err) {
      return { 
        output: `<strong style="color: #ff4d4f;">${t('error')}:</strong> ${err.message}`,
        success: false
      };
    }
  }, [t]);

  // Run code with loading state
  const runCode = useCallback((codeToRun = codeRef.current) => {
    setIsRunning(true);
    const result = executeCode(codeToRun);
    setOutput(result.output);
    outputRef.current = result.output;
    setIsRunning(false);
    return result.output;
  }, [executeCode]);

  // Auto-run with debounce when code changes
  useEffect(() => {
    if (isSharedView) return; // Don't auto-run for shared views
    
    // Clear previous timeout
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
    }
    
    // Set new timeout for debounced execution
    runTimeoutRef.current = setTimeout(() => {
      if (code.trim()) {
        runCode();
      } else {
        setOutput("");
        outputRef.current = "";
      }
    }, 500); // 500ms debounce
    
    return () => {
      if (runTimeoutRef.current) {
        clearTimeout(runTimeoutRef.current);
      }
    };
  }, [code, isSharedView, runCode]);

  // Load shared code when URL has share parameter
  const loadSharedCode = useCallback(async (shareId) => {
    try {
      setLoading(prev => ({ ...prev, loadingShare: true }));
      setIsSharedView(true);
      
      const response = await api.get(`/js-playground/share/${shareId}`);
      const data = response.data;
      
      if (data.code) {
        setCode(data.code);
        codeRef.current = data.code;
      }
      
      if (data.output) {
        setOutput(data.output);
        outputRef.current = data.output;
      }
      
      toast.success(t('shared_code_loaded'));
    } catch (error) {
      toast.error(t('failed_to_load_shared_code'));
    } finally {
      setLoading(prev => ({ ...prev, loadingShare: false }));
    }
  }, [t]);

  // Check for share parameter on mount
  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId) {
      loadSharedCode(shareId);
    }
  }, []); // Only run on mount

  const loadHistory = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, history: true }));
      setError(null);
      
      if (!isAuthenticated()) {
        setHistory([]);
        return;
      }
      
      const response = await api.get("/js-playground/history");
      
      if (response.data && Array.isArray(response.data.history)) {
        setHistory(response.data.history);
      } else {
        setHistory([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError(t('authentication_required'));
        toast.error(t('authentication_required'));
      } else {
        setError(t('failed_to_load_history'));
        toast.error(t('failed_to_load_history'));
      }
      setHistory([]);
    } finally {
      setLoading(prev => ({ ...prev, history: false }));
    }
  }, [t]);

  const loadShares = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, shares: true }));
      setError(null);
      
      if (!isAuthenticated()) {
        setShares([]);
        return;
      }
      
      const response = await api.get("/js-playground/shares");
      
      if (response.data && Array.isArray(response.data.shares)) {
        setShares(response.data.shares || []);
      } else {
        setShares([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('authentication_required'));
      } else {
        toast.error(t('failed_to_load_shares'));
      }
      setShares([]);
    } finally {
      setLoading(prev => ({ ...prev, shares: false }));
    }
  }, [t]);

  const saveToHistory = async () => {
    try {
      if (!isAuthenticated()) {
        toast.error(t('login_to_save_history'));
        return;
      }

      setLoading(prev => ({ ...prev, save: true }));
      setError(null);
      
      // Ensure we have output
      const currentOutput = outputRef.current || runCode();
      
      await api.post("/js-playground/history", {
        code: codeRef.current,
        output: currentOutput
      });
      
      // Reload history if modal is open
      if (historyVisible) {
        await loadHistory();
      }
      
      toast.success(t('saved_to_history'));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('authentication_required'));
      } else {
        toast.error(t('failed_to_save_history'));
      }
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  const handleManualSave = async () => {
    if (!codeRef.current.trim()) {
      toast.error(t('code_empty'));
      return;
    }
    
    await saveToHistory();
  };

  const handleManualRun = () => {
    runCode();
  };

  const handleCopyOutput = async () => {
    try {
      // Remove HTML tags for plain text copy
      const plainOutput = outputRef.current.replace(/<[^>]*>/g, '');
      await navigator.clipboard.writeText(plainOutput);
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(codeRef.current);
      toast.success(t('code_copied'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleShare = async () => {
    try {
      setLoading(prev => ({ ...prev, share: true }));
      
      // Ensure we have the latest code and output
      const currentCode = codeRef.current;
      const currentOutput = outputRef.current || runCode();
      
      const response = await api.post(`/js-playground/share`, {
        code: currentCode,
        output: currentOutput,
      });

      const data = response.data;
      
      if (data.id || data.share_id) {
        const shareId = data.id || data.share_id;
        const url = `${window.location.origin}/tools/js-playground?share=${shareId}`;
        await navigator.clipboard.writeText(url);
        toast.success(
          <div>
            <p>{t('share_success')}</p>
            <p className="text-xs mt-1">{t('link_copied')}</p>
          </div>
        );
        
        // Reload shares list
        await loadShares();
      } else {
        toast.error(t('share_failed'));
      }
    } catch (err) {
      toast.error(t('share_failed'));
    } finally {
      setLoading(prev => ({ ...prev, share: false }));
    }
  };

  const handleLoadFromHistory = (historyItem) => {
    setCode(historyItem.code);
    setOutput(historyItem.output);
    codeRef.current = historyItem.code;
    outputRef.current = historyItem.output;
    setIsSharedView(false); // Not a shared view
    setHistoryVisible(false);
    toast.success(t('history_loaded'));
  };

  const handleDeleteHistory = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/js-playground/history/${id}`);
      toast.success(t('history_deleted'));
      await loadHistory();
    } catch (error) {
      toast.error(t('failed_to_delete_history'));
    }
  };

  const handleClearAllHistory = async () => {
    try {
      await api.delete("/js-playground/history");
      toast.success(t('all_history_deleted'));
      await loadHistory();
    } catch (error) {
      toast.error(t('failed_to_clear_history'));
    }
  };

  const handleDeleteShare = async (shareId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/js-playground/share/${shareId}`);
      toast.success(t('share_deleted'));
      await loadShares();
    } catch (error) {
      toast.error(t('failed_to_delete_share'));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleOpenHistoryModal = async () => {
    setHistoryVisible(true);
    setError(null);
    await Promise.all([loadHistory(), loadShares()]);
  };

  const handleClearSharedView = () => {
    setIsSharedView(false);
    // Clear the share parameter from URL
    navigate("/tools/js-playground", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          {/* Title and buttons in single line */}
          <div className="flex flex-row justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              
              {isSharedView && (
                <Badge 
                  count={t('shared')}
                  color="blue"
                  style={{ fontSize: '12px', fontWeight: 'normal' }}
                />
              )}
            </div>
            
            {/* All buttons in a single row */}
            <Space wrap className="flex-nowrap whitespace-nowrap">
              <StandardButton
                type="secondary"
                onClick={handleOpenHistoryModal}
                size="middle"
              >
                {t('history_and_shares')}
              </StandardButton>
              
              <Tooltip title={t('save_to_history')}>
                <StandardButton
                  type="secondary"
                  onClick={handleManualSave}
                  disabled={!code.trim() || loading.save || !isAuthenticated()}
                  size="middle"
                >
                  {t('save')}
                </StandardButton>
              </Tooltip>
              
              <Tooltip title={t('run_code')}>
                <StandardButton
                  type="secondary"
                  onClick={handleManualRun}
                  disabled={isRunning}
                  size="middle"
                >
                  {t('run')}
                </StandardButton>
              </Tooltip>
              
              <Tooltip title={t('share_code')}>
                <StandardButton
                  type="secondary"
                  onClick={handleShare}
                  disabled={loading.share}
                  size="middle"
                >
                  {t('share')}
                </StandardButton>
              </Tooltip>
            </Space>
          </div>

          {/* Shared View Alert */}
          {isSharedView && (
            <Alert
              message={
                <Space>
                  <span>🔗</span>
                  <span>{t('shared_view')}</span>
                </Space>
              }
              description={t('shared_view_description')}
              type="info"
              showIcon
              action={
                <StandardButton type="secondary" size="small" onClick={handleClearSharedView}>
                  {t('clear')}
                </StandardButton>
              }
              className="mb-4"
            />
          )}

          {loading.loadingShare && (
            <div className="text-center py-4">
              <Spin />
              <p className="mt-2">{t('loading_shared_code')}</p>
            </div>
          )}

          {error && (
            <Alert
              message={t('error')}
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              className="mb-4"
            />
          )}

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold mb-2">{t('code')}</h3>
              <Space>
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={handleCopyCode}
                >
                  {t('copy_code')}
                </StandardButton>
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={() => {
                    setCode('');
                    codeRef.current = '';
                    setOutput('');
                    outputRef.current = '';
                    setIsSharedView(false);
                  }}
                  danger
                >
                  {t('clear')}
                </StandardButton>
              </Space>
            </div>
            <TextArea
              rows={10}
              value={code}
              onChange={(e) => {
                const newCode = e.target.value;
                setCode(newCode);
                codeRef.current = newCode;
                setIsSharedView(false); // User is editing, so it's no longer a shared view
              }}
              placeholder={t('js_playground_placeholder')}
              className="font-mono"
              disabled={loading.loadingShare}
            />
            
            {isRunning && (
              <div className="flex items-center gap-2 text-blue-600">
                <span className="animate-spin">⟳</span>
                <span>{t('running_code')}</span>
              </div>
            )}
          </div>

          {output && (
            <div className="mt-6 bg-white p-4 border rounded shadow relative">
              <div 
                dangerouslySetInnerHTML={{ __html: output }} 
                className="font-mono text-sm whitespace-pre-wrap break-words"
              />
              <div className="mt-4 flex flex-wrap gap-2">
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={handleCopyOutput}
                >
                  {t('copy_output')}
                </StandardButton>
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={() => {
                    setOutput('');
                    outputRef.current = '';
                  }}
                  danger
                >
                  {t('clear_output')}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>
        
        {/* History and Shares Modal */}
        <Modal
          title={t('history_and_shares')}
          open={historyVisible}
          onCancel={() => setHistoryVisible(false)}
          width={800}
          footer={null}
        >
          <div className="space-y-6">
            {/* Authentication status */}
            {!isAuthenticated() && (
              <Alert
                message={t('authentication_required')}
                description={t('login_to_view_history_shares')}
                type="warning"
                showIcon
              />
            )}

            {/* History Section */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">{t('history')}</h3>
                {history.length > 0 && (
                  <StandardButton
                    type="secondary"
                    size="small"
                    onClick={handleClearAllHistory}
                    danger
                  >
                    {t('clear_all')}
                  </StandardButton>
                )}
              </div>
              {loading.history ? (
                <div className="text-center py-8">
                  <Spin />
                  <p className="mt-2">{t('loading_history')}</p>
                </div>
              ) : (
                <List
                  dataSource={history}
                  locale={{ emptyText: isAuthenticated() ? t('no_history') : t('login_to_view_history') }}
                  renderItem={(item) => (
                    <List.Item
                      className="cursor-pointer hover:bg-gray-50 p-2 rounded"
                      onClick={() => handleLoadFromHistory(item)}
                      actions={[
                        <StandardButton
                          type="secondary"
                          size="small"
                          onClick={(e) => handleDeleteHistory(item.id, e)}
                          danger
                        >
                          {t('delete')}
                        </StandardButton>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <div className="flex items-center gap-2">
                            <span>🕒</span>
                            <span>{formatDate(item.timestamp)}</span>
                          </div>
                        }
                        description={
                          <div>
                            <div className="font-mono text-sm bg-gray-100 p-2 rounded mb-2">
                              {truncateText(item.code, 150)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {t('click_to_load')}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </div>

            {/* Shares Section */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">{t('shared_snippets')}</h3>
                <Text type="secondary" className="text-sm">
                  {shares.length} {t('items')}
                </Text>
              </div>
              {loading.shares ? (
                <div className="text-center py-8">
                  <Spin />
                  <p className="mt-2">{t('loading_shares')}</p>
                </div>
              ) : (
                <List
                  dataSource={shares}
                  locale={{ emptyText: isAuthenticated() ? t('no_shares') : t('login_to_view_shares') }}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <StandardButton
                          type="secondary"
                          size="small"
                          onClick={(e) => handleDeleteShare(item.id, e)}
                        >
                          {t('delete')}
                        </StandardButton>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <div className="flex items-center gap-2">
                            <span>👁️</span>
                            <span>{item.views ?? 0} {t('views')}</span>
                            {item.isExpired && (
                              <Tag color="red" size="small">{t('expired')}</Tag>
                            )}
                            <Tag color="blue" size="small">{formatDate(item.timestamp)}</Tag>
                          </div>
                        }
                        description={
                          <div>
                            <div className="font-mono text-sm bg-gray-100 p-2 rounded mb-2">
                              {truncateText(item.code, 100)}
                            </div>
                            <Space wrap>
                              <StandardButton
                                type="secondary"
                                size="small"
                                onClick={() => {
                                  navigate(`/tools/js-playground?share=${item.id}`);
                                  setHistoryVisible(false);
                                }}
                              >
                                {t('open')}
                              </StandardButton>
                              <StandardButton
                                type="secondary"
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    `${window.location.origin}/tools/js-playground?share=${item.id}`
                                  );
                                  toast.success(t('copied_to_clipboard'));
                                }}
                              >
                                {t('copy_link')}
                              </StandardButton>
                              <StandardButton
                                type="secondary"
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(item.code);
                                  toast.success(t('code_copied'));
                                }}
                              >
                                {t('copy_code_short')}
                              </StandardButton>
                            </Space>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </div>
          </div>
        </Modal>

        <Footer />
      </div>
    </div>
  );
};

export default JSPlayground;